import React from 'react';
import { Shield } from 'lucide-react';
import { Role, Permission } from '../../types';
import { Theme } from '../../ThemeContext';

interface RoleEditModeProps {
  role: Role | null;
  permissions: Permission[];
  theme: Theme;
  editName?: string;
  editDescription: string;
  editPermissions: string[];
  onNameChange?: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onPermissionsChange: (value: string[]) => void;
}

const RoleEditMode: React.FC<RoleEditModeProps> = ({
  role,
  permissions,
  theme,
  editName,
  editDescription,
  editPermissions,
  onNameChange,
  onDescriptionChange,
  onPermissionsChange,
}) => {
  const isCreating = !role;
  // Group permissions by resource type
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource_type]) {
      acc[perm.resource_type] = [];
    }
    acc[perm.resource_type].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  const handlePermissionToggle = (permissionId: string) => {
    if (editPermissions.includes(permissionId)) {
      onPermissionsChange(editPermissions.filter(id => id !== permissionId));
    } else {
      onPermissionsChange([...editPermissions, permissionId]);
    }
  };

  const handleSelectAllForResource = (resourceType: string) => {
    const resourcePermissions = groupedPermissions[resourceType];
    const allSelected = resourcePermissions.every(p => editPermissions.includes(p.permission_id));
    
    if (allSelected) {
      // Deselect all for this resource
      const remainingPermissions = editPermissions.filter(
        id => !resourcePermissions.find(p => p.permission_id === id)
      );
      onPermissionsChange(remainingPermissions);
    } else {
      // Select all for this resource
      const newPermissions = [...editPermissions];
      resourcePermissions.forEach(p => {
        if (!newPermissions.includes(p.permission_id)) {
          newPermissions.push(p.permission_id);
        }
      });
      onPermissionsChange(newPermissions);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Basic Information */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0 }}>
          Basic Information
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Role Name
            </label>
            {isCreating ? (
              <>
                <input
                  type="text"
                  value={editName || ''}
                  onChange={(e) => onNameChange?.(e.target.value)}
                  placeholder="Enter role name (e.g., analyst, manager)..."
                  style={{
                    width: '100%',
                    padding: 12,
                    background: theme.background,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 4,
                    color: theme.text,
                    fontSize: 16,
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                  Use lowercase letters, numbers, and underscores
                </div>
              </>
            ) : (
              <>
                <div style={{ 
                  color: theme.text, 
                  fontSize: 16, 
                  textTransform: 'uppercase', 
                  fontWeight: 600,
                  padding: 12,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 4,
                }}>
                  {role?.name}
                </div>
                <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 4 }}>
                  Role name cannot be changed
                </div>
              </>
            )}
          </div>

          <div>
            <label style={{ color: theme.textSecondary, fontSize: 14, fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Description
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Enter role description..."
              style={{
                width: '100%',
                minHeight: 100,
                padding: 12,
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: 4,
                color: theme.text,
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 24,
      }}>
        <h2 style={{ color: theme.text, fontSize: 18, marginBottom: 16, marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={20} />
          Permissions ({editPermissions.length} selected)
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(groupedPermissions).map(([resourceType, perms]) => {
            const allSelected = perms.every(p => editPermissions.includes(p.permission_id));
            const someSelected = perms.some(p => editPermissions.includes(p.permission_id));
            
            return (
              <div key={resourceType}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: 12 
                }}>
                  <h3 style={{ 
                    color: theme.text, 
                    fontSize: 16, 
                    margin: 0,
                    textTransform: 'capitalize',
                    fontWeight: 600 
                  }}>
                    {resourceType}
                  </h3>
                  <button
                    onClick={() => handleSelectAllForResource(resourceType)}
                    style={{
                      padding: '4px 12px',
                      background: 'transparent',
                      color: theme.accent,
                      border: `1px solid ${theme.accent}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                  {perms.map(perm => {
                    const isSelected = editPermissions.includes(perm.permission_id);
                    
                    return (
                      <label
                        key={perm.permission_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 12px',
                          background: isSelected ? theme.activeBackground : theme.background,
                          border: `1px solid ${isSelected ? theme.accent : theme.border}`,
                          borderRadius: 6,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handlePermissionToggle(perm.permission_id)}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: 'pointer',
                          }}
                        />
                        <span style={{ 
                          color: isSelected ? theme.activeText : theme.text, 
                          fontSize: 13, 
                          fontWeight: isSelected ? 600 : 500,
                          textTransform: 'uppercase',
                          flex: 1,
                        }}>
                          {perm.action}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RoleEditMode;
